import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { visibleQuestions } from "../lib/assessment";
import { LogoLockup, LogoSeal, Motif } from "../components/brand/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, ArrowRight, Check, Cloud } from "lucide-react";

const EASE = [0.4, 0, 0.2, 1];

function TrailProgress({ pct }) {
  const dash = 300;
  return (
    <svg viewBox="0 0 300 14" className="w-full h-3" preserveAspectRatio="none" aria-hidden="true">
      <path d="M2 9 C60 5 90 11 150 8 C210 5 250 10 298 7" fill="none" stroke="rgba(118,83,59,0.25)" strokeWidth="2" />
      <path className="trail-line" d="M2 9 C60 5 90 11 150 8 C210 5 250 10 298 7" fill="none" stroke="#A65F42"
        strokeWidth="2.4" strokeLinecap="round" strokeDasharray={dash} strokeDashoffset={dash - dash * pct} />
    </svg>
  );
}

export default function Assessment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [milestone, setMilestone] = useState(null);
  const [done, setDone] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [exitOpen, setExitOpen] = useState(false);
  const saveTimers = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/intake/${token}`);
        setQuestions(data.questions);
        setSections(data.sections);
        setAnswers(data.answers || {});
        if (data.submitted) {
          setDone(true);
          localStorage.removeItem("lr_intake_token");
        } else {
          localStorage.setItem("lr_intake_token", token);
        }
      } catch {
        toast.error("This assessment link could not be found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const visible = useMemo(() => visibleQuestions(questions, answers), [questions, answers]);
  const current = visible[Math.min(idx, visible.length - 1)];
  const sectionMap = useMemo(() => Object.fromEntries(sections.map((s) => [s.key, s])), [sections]);
  const pct = visible.length ? Math.min(1, (idx + 1) / visible.length) : 0;
  const currentSectionTitle = current ? sectionMap[current.section]?.title : "";

  const persist = useCallback((qid, wording, value) => {
    setSaveState("saving");
    api.patch(`/public/intake/${token}/answer`, { question_id: qid, question_wording: wording, answer: value })
      .then(() => setSaveState("saved"))
      .catch(() => setSaveState("idle"));
  }, [token]);

  const setAnswer = (qid, value, wording, immediate = false) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    if (immediate) {
      persist(qid, wording, value);
    } else {
      clearTimeout(saveTimers.current[qid]);
      saveTimers.current[qid] = setTimeout(() => persist(qid, wording, value), 600);
    }
  };

  const canAdvance = () => {
    if (!current) return false;
    if (!current.required) return true;
    const a = answers[current.id];
    return a != null && a !== "" && !(Array.isArray(a) && a.length === 0);
  };

  const goNext = () => {
    if (current) persist(current.id, current.prompt, answers[current.id]);
    const nextList = visibleQuestions(questions, answers);
    const pos = nextList.findIndex((q) => q.id === current.id);
    const next = nextList[pos + 1];
    if (!next) return submit();
    const leavingSection = current.section;
    if (next.section !== leavingSection) {
      const s = sectionMap[leavingSection];
      if (s?.milestone) {
        setMilestone(s);
        setTimeout(() => {
          setMilestone(null);
          setDir(1);
          setIdx(pos + 1);
        }, reduce ? 300 : 1400);
        return;
      }
    }
    setDir(1);
    setIdx(pos + 1);
  };

  const goBack = () => {
    if (idx === 0) return;
    setDir(-1);
    setIdx(idx - 1);
  };

  const submit = async () => {
    try {
      await api.post(`/public/intake/${token}/submit`);
      localStorage.removeItem("lr_intake_token");
      setDone(true);
    } catch {
      toast.error("Could not submit. Your answers are saved. Please try again.");
    }
  };

  const saveAndExit = () => {
    if (current) persist(current.id, current.prompt, answers[current.id]);
    setExitOpen(true);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-lr-leather">Loading…</div>;

  if (done) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center paper-grain">
        <LogoSeal size={240} />
        <h1 className="font-serif text-3xl md:text-4xl text-lr-charcoal mt-6">That's everything I need to begin.</h1>
        <p className="text-lr-leather mt-4 max-w-md text-lg">
          Thank you for taking the time. I'll investigate the rest and be in touch with what I find.
        </p>
        <p className="text-sm text-lr-leather/70 mt-8">You can close this window.</p>
      </div>
    );
  }

  const variants = reduce
    ? { enter: { opacity: 1 }, center: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        enter: (d) => ({ opacity: 0, x: d > 0 ? 24 : -24 }),
        center: { opacity: 1, x: 0 },
        exit: (d) => ({ opacity: 0, x: d > 0 ? -24 : 24 }),
      };

  return (
    <div className="min-h-screen flex flex-col paper-grain">
      <header className="w-full border-b hairline bg-lr-cream/70 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <LogoLockup compact />
          <div className="flex items-center gap-4">
            <span data-testid="save-indicator" className="flex items-center gap-1.5 text-xs text-lr-leather">
              <Cloud className="h-3.5 w-3.5" />
              {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Progress saved" : "Autosaving"}
            </span>
            <button onClick={saveAndExit} data-testid="save-finish-later"
              className="text-xs text-lr-olive hover:underline whitespace-nowrap">
              Save and finish later
            </button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-5 pb-2"><TrailProgress pct={pct} /></div>
      </header>

      <main className="flex-1 flex flex-col">
        <div className="w-full max-w-2xl mx-auto px-5 py-8 md:py-14 flex-1 flex flex-col">
          <AnimatePresence mode="wait" custom={dir}>
            {milestone ? (
              <motion.div key="ms" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.24, ease: EASE }} className="flex-1 flex flex-col items-center justify-center text-center py-16">
                <Motif kind={milestone.motif || "sun"} size={130} />
                <p className="font-serif text-2xl md:text-3xl text-lr-charcoal mt-6 max-w-lg">{milestone.milestone}</p>
              </motion.div>
            ) : current ? (
              <motion.div key={current.id} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.24, ease: EASE }} className="flex-1 flex flex-col">
                <QuestionView q={current} value={answers[current.id]} onChange={setAnswer} onEnter={() => canAdvance() && goNext()} />
              </motion.div>
            ) : null}
          </AnimatePresence>

          {!milestone && current && (
            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={goBack} disabled={idx === 0} data-testid="assessment-back"
                className="text-lr-leather hover:text-lr-olive hover:bg-transparent disabled:opacity-30">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
              </Button>
              <Button onClick={goNext} disabled={!canAdvance()} data-testid="assessment-next"
                className="h-12 px-7 bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px] group">
                {idx + 1 >= visible.length ? (<>Finish <Check className="h-4 w-4 ml-1.5" /></>) : (<>Continue <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" /></>)}
              </Button>
            </div>
          )}
        </div>
      </main>

      <Dialog open={exitOpen} onOpenChange={setExitOpen}>
        <DialogContent className="bg-lr-card" data-testid="exit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Your progress is saved</DialogTitle>
            <DialogDescription>
              Everything you have answered so far is saved. You can return on this device anytime and pick up right where you left off.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExitOpen(false)} className="rounded-[4px]">Keep going</Button>
            <Button onClick={() => navigate("/")} data-testid="exit-confirm" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
              Finish later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionView({ q, value, onChange, onEnter }) {
  return (
    <div className="flex flex-col">
      <h2 className="font-serif text-2xl md:text-3xl leading-snug text-lr-charcoal">{q.prompt}</h2>
      {q.helper && <p className="mt-3 text-lr-leather leading-relaxed">{q.helper}</p>}
      <div className="mt-7">
        {q.type === "text" && (
          <Input autoFocus data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(q.id, e.target.value, q.prompt)}
            onKeyDown={(e) => e.key === "Enter" && onEnter()} className="h-14 text-lg bg-white/60 border-lr-leather/30" placeholder="Type your answer…" />
        )}
        {q.type === "textarea" && (
          <Textarea autoFocus data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(q.id, e.target.value, q.prompt)}
            rows={5} className="text-lg bg-white/60 border-lr-leather/30 leading-relaxed" placeholder="Take your time…" />
        )}
        {q.type === "select" && (
          <div className="space-y-2.5">
            {q.options.map((o) => {
              const sel = value === o.value;
              return (
                <button key={o.value} type="button" data-testid={`q-${q.id}-${o.value}`}
                  onClick={() => onChange(q.id, o.value, q.prompt, true)}
                  className={`w-full text-left px-5 py-4 rounded-[4px] border transition-colors text-base ${sel ? "bg-lr-olive text-lr-cream border-lr-olive" : "bg-white/50 border-lr-leather/25 text-lr-charcoal hover:border-lr-olive/60 hover:bg-white/80"}`}>
                  <span className="inline-flex items-center gap-3">
                    <span className={`h-4 w-4 rounded-full border flex-shrink-0 ${sel ? "border-lr-cream bg-lr-cream" : "border-lr-leather/50"}`} />
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {q.type === "multi" && (
          <div className="space-y-2.5">
            {q.max_select && <p className="text-sm text-lr-clay mb-2">Choose up to {q.max_select}.</p>}
            {q.options.map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const sel = arr.includes(o.value);
              const toggle = () => {
                let next;
                if (sel) next = arr.filter((v) => v !== o.value);
                else if (q.max_select && arr.length >= q.max_select) { toast.info(`You can choose up to ${q.max_select}.`); return; }
                else next = [...arr, o.value];
                onChange(q.id, next || [], q.prompt, true);
              };
              return (
                <button key={o.value} type="button" data-testid={`q-${q.id}-${o.value}`} onClick={toggle}
                  className={`w-full text-left px-5 py-3.5 rounded-[4px] border transition-colors text-base ${sel ? "bg-lr-olive text-lr-cream border-lr-olive" : "bg-white/50 border-lr-leather/25 text-lr-charcoal hover:border-lr-olive/60 hover:bg-white/80"}`}>
                  <span className="inline-flex items-center gap-3">
                    <span className={`h-4 w-4 rounded-[3px] border flex items-center justify-center flex-shrink-0 ${sel ? "border-lr-cream bg-lr-cream" : "border-lr-leather/50"}`}>
                      {sel && <Check className="h-3 w-3 text-lr-olive" />}
                    </span>
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
